<script lang="ts" setup>
import {IconPosition} from "~/enums/IconPositionEnum";
// Explicit import required: the auto-import name for components/Buttons/
// is ButtonsInternalRedirectButton, so the bare tag resolves to nothing.
import InternalRedirectButton from "~/components/Buttons/InternalRedirectButton.vue";

usePageSeo('seoBookingTitle', 'seoBookingDescription');

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (!element) return;

  // scrollIntoView is JS, so the CSS reduced-motion rule does not reach it.
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  element.scrollIntoView({behavior: reduceMotion ? 'auto' : 'smooth', block: 'center'});

  // Carry the keyboard caret along with the viewport, otherwise the next Tab
  // continues from the shortcut instead of the section it jumped to.
  element.focus({preventScroll: true});
};
</script>

<template>
  <div class="booking-page">
    <Hero 
      :title="$t('Booking')" 
      :subtitle="$t('bookingSubtitle')"
      backgroundImage="https://res.cloudinary.com/dlct0dnwt/image/upload/q_auto:eco/v1684614497/refuge/inside/sleepingArea_1_sdpz17.jpg"
    />

    <div class="content-container">
      <!-- Quick Info Highlights -->
      <section class="highlights-section">
        <h2 class="highlights-title">{{ $t('quickHighlights') }}</h2>
        <div class="highlights-grid">
          <button type="button" class="highlight-card" @click="scrollToSection('pricing')">
            <Icon name="ph:house-bold" class="highlight-icon" />
            <div class="highlight-info">
              <span class="highlight-label">{{ $t('highlightAccommodationTitle') }}</span>
              <span class="highlight-value">{{ $t('highlightAccommodationValue') }}</span>
            </div>
          </button>
          <button type="button" class="highlight-card" @click="scrollToSection('pricing')">
            <Icon name="ph:tent-bold" class="highlight-icon" />
            <div class="highlight-info">
              <span class="highlight-label">{{ $t('highlightTentTitle') }}</span>
              <span class="highlight-value">{{ $t('highlightTentValue') }}</span>
            </div>
          </button>
          <button type="button" class="highlight-card" @click="scrollToSection('seasons')">
            <Icon name="ph:calendar-bold" class="highlight-icon" />
            <div class="highlight-info">
              <span class="highlight-label">{{ $t('highlightSeasonTitle') }}</span>
              <span class="highlight-value">{{ $t('highlightSeasonValue') }}</span>
            </div>
          </button>
          <button type="button" class="highlight-card" @click="scrollToSection('refunds')">
            <Icon name="ph:clock-counter-clockwise-bold" class="highlight-icon" />
            <div class="highlight-info">
              <span class="highlight-label">{{ $t('highlightCancellationTitle') }}</span>
              <span class="highlight-value">{{ $t('highlightCancellationValue') }}</span>
            </div>
          </button>
          <button type="button" class="highlight-card" @click="scrollToSection('booking-contact')">
            <Icon name="ph:users-bold" class="highlight-icon" />
            <div class="highlight-info">
              <span class="highlight-label">{{ $t('highlightCapacityTitle') }}</span>
              <span class="highlight-value">{{ $t('highlightCapacityValue') }}</span>
            </div>
          </button>

        </div>
      </section>

      <!-- Direct Contact -->
      <section id="booking-contact" tabindex="-1" class="section">
        <h2 class="section-title">{{ $t('howToBook') }}</h2>
        <p class="section-desc">{{ $t('bookingStrategy') }}</p>
        <ContactInformation />
        <p class="disclaimer">{{ $t('bookingDisclaimer') }}</p>
      </section>

      <div class="info-grid">
        <!-- Seasons -->
        <section id="seasons" tabindex="-1" class="info-group">
          <h3 class="group-title">{{ $t('seasons') }}</h3>
          <div class="seasons-list">
            <div class="season-entry">
              <div class="season-main">
                <span class="season-name">{{ $t('mainSeason') }}</span>
                <span class="season-dates">{{ $t('juneOctober') }}</span>
              </div>
              <span class="status open">{{ $t('openEveryDay') }}</span>
            </div>
            <div class="season-entry">
              <div class="season-main">
                <span class="season-name">{{ $t('winter') }}</span>
                <span class="season-dates">{{ $t('novemberMay') }}</span>
              </div>
              <span class="status arrangement">{{ $t('onlyWeekends') }}</span>
            </div>
          </div>
          <p class="note">{{ $t('seasonExplanation') }}</p>
        </section>

        <!-- Pricing -->
        <section id="pricing" tabindex="-1" class="info-group">
          <h3 class="group-title">{{ $t('overnightPricing') }}</h3>
          <div class="pricing-table">
            <div class="price-row">
              <span>{{ $t('AccommodationWithFood') }}</span>
              <span class="value">{{ $t('accommodationPrice') }}</span>
            </div>
            <div class="price-row">
              <span>{{ $t('Camping') }}</span>
              <span class="value">{{ $t('highlightTentValue') }}</span>
            </div>
          </div>
          <div class="notes-container">
            <p class="note">{{ $t('sleepCosts') }}</p>
            <p class="note">{{ $t('tentCost') }}</p>
          </div>
        </section>

        <!-- Payment -->
        <section class="info-group">
          <h3 class="group-title">{{ $t('payment') }}</h3>
          <p class="plain-text">{{ $t('paymentInfo') }}</p>
        </section>

        <!-- Refunds -->
        <section id="refunds" tabindex="-1" class="info-group">
          <h3 class="group-title">{{ $t('refunds') }}</h3>
          <div class="plain-text">
            <p>{{ $t('refundInfo1') }}</p>
            <p>{{ $t('refundInfo2') }}</p>
          </div>
        </section>
      </div>

      <!-- Everything a booking raises that is not about price or dates -->
      <section class="faq-link">
        <h3 class="group-title">{{ $t('faqMoreQuestions') }}</h3>
        <p class="plain-text">{{ $t('faqMoreQuestionsText') }}</p>
        <InternalRedirectButton
          :text="$t('faqTitle')"
          redirect="/refuge/faq"
          icon="ph:question"
          :iconPosition="IconPosition.Left"
        />
      </section>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.booking-page
  width: 100%

.content-container
  max-width: 1100px
  margin: 0 auto
  padding: 0 2rem 8rem

.section
  margin-bottom: 5rem
  text-align: center

  &:focus
    outline: none

.section-title
  font-size: 2.5rem
  font-weight: 800
  margin-bottom: 1.25rem

.section-desc
  font-size: 1.25rem
  line-height: 1.6
  opacity: 0.8
  max-width: 800px
  margin: 0 auto

.disclaimer
  font-size: 1rem
  opacity: 0.5
  font-style: italic

// Highlights
.highlights-section
  margin-bottom: 6rem
  text-align: center

.highlights-title
  font-size: 1.5rem
  font-weight: 800
  text-transform: uppercase
  letter-spacing: 2px
  margin-bottom: 2.5rem
  opacity: 0.6

.highlights-grid
  display: grid
  grid-template-columns: repeat(5, 1fr)
  gap: 2rem

.highlight-card
  width: 100%
  border: none
  font: inherit
  color: inherit
  text-align: center
  display: flex
  flex-direction: column
  align-items: center
  gap: 1.25rem
  padding: 2.5rem 1.5rem
  background: white
  border-radius: 20px
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05)
  transition: transform 0.3s ease, box-shadow 0.3s ease
  cursor: pointer

  &:hover
    transform: translateY(-5px)
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1)

.dark-mode .highlight-card
  background: rgba(255, 255, 255, 0.03)
  box-shadow: none
  border: 1px solid rgba(255, 255, 255, 0.05)

.highlight-icon
  font-size: 2.5rem
  color: $color-primary

.highlight-info
  display: flex
  flex-direction: column
  gap: 0.25rem

.highlight-label
  font-size: 0.9rem
  font-weight: 700
  text-transform: uppercase
  letter-spacing: 1px
  opacity: 0.5

.highlight-value
  font-size: 1.25rem
  font-weight: 800

.faq-link
  display: flex
  flex-direction: column
  align-items: center
  gap: 1.25rem
  margin-top: 5rem
  padding-top: 5rem
  border-top: 1px solid rgba(0, 0, 0, 0.05)
  text-align: center

.dark-mode .faq-link
  border-top-color: rgba(255, 255, 255, 0.05)

// Info Grid
.info-grid
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 5rem 8rem
  border-top: 1px solid rgba(0, 0, 0, 0.05)
  padding-top: 5rem

.dark-mode .info-grid
  border-top-color: rgba(255, 255, 255, 0.05)

.info-group
  display: flex
  flex-direction: column
  gap: 2rem

  &:focus
    outline: none

.group-title
  font-size: 1.5rem
  font-weight: 800
  text-transform: uppercase
  letter-spacing: 1.5px
  color: $color-primary

// Seasons
.seasons-list
  display: flex
  flex-direction: column
  gap: 1.25rem

.season-entry
  display: flex
  justify-content: space-between
  align-items: center
  padding: 1.25rem 1.5rem
  background-color: rgba(0, 0, 0, 0.02)
  border-radius: 12px

.dark-mode .season-entry
  background-color: rgba(255, 255, 255, 0.03)

.season-main
  display: flex
  flex-direction: column

.season-name
  font-weight: 800
  font-size: 1.1rem

.season-dates
  font-size: 0.95rem
  opacity: 0.6

.status
  font-size: 0.85rem
  font-weight: 800
  text-transform: uppercase
  letter-spacing: 0.5px
  
  &.open
    color: #287b5d
  &.arrangement
    color: #b45d00

// Pricing
.pricing-table
  display: flex
  flex-direction: column

.price-row
  display: flex
  justify-content: space-between
  padding: 1rem 0
  border-bottom: 1px solid rgba(0, 0, 0, 0.05)
  font-weight: 700
  font-size: 1.1rem

.dark-mode .price-row
  border-bottom-color: rgba(255, 255, 255, 0.05)

.value
  font-weight: 800
  color: $color-primary

.notes-container
  display: flex
  flex-direction: column
  gap: 0.75rem

.plain-text, .note
  font-size: 1.1rem
  line-height: 1.7
  opacity: 0.8
  margin: 0

.note
  font-size: 0.95rem
  opacity: 0.5

@media (max-width: 1280px)
  .highlights-grid
    grid-template-columns: repeat(3, 1fr)
    gap: 1.5rem

@media (max-width: 1024px)
  .highlights-grid
    grid-template-columns: 1fr 1fr
    gap: 1.5rem

@media (max-width: 768px)
  .info-grid
    grid-template-columns: 1fr
    gap: 4rem

  .section-title
    font-size: 2rem

  .highlights-grid
    display: flex
    flex-wrap: wrap
    justify-content: center
    gap: 0.75rem
    width: 90%
    margin: 0 auto

  .highlight-card
    flex: 0 0 calc(50% - 0.375rem)
    padding: 1.5rem 1rem
    gap: 0.75rem

  .highlights-section
    margin-bottom: 4rem

  .highlight-icon
    font-size: 1.75rem

  .highlight-value
    font-size: 1rem
</style>
